import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Tooltip,
  Radio,
  Segmented,
  Card,
  Space,
  Modal,
  Upload,
  Switch,
} from "antd";
import {
  InfoCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { getAccountDetails, updateAccountDetails } from "Sdk/Actions";
import { API } from "aws-amplify";
import { message } from "antd";
import AISuggestionsPanel from "./AISuggestionsPanel";
import FormBuilder from "./FormBuilder";
import { Loader } from "@/Components/Loader";
import { uploadProfilePic } from "Utility/UploadFile";
import UploadProfilePic from "@/Components/UploadProfilePic";

// Debounce function to limit the rate of updates
const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

// Define debounced function ONCE (no useCallback needed)
const debouncedUpdateAccountDetails = debounce(async (updatedData) => {
  await updateAccountDetails(updatedData);
  message.success("Logo saved successfully!");
}, 1000);

const languages = [
  { label: "English", value: "English" },
  { label: "Hindi", value: "Hindi" },
  { label: "Gujarati", value: "Gujarati" },
];

const peopleOptions = [
  { label: "1", value: "1" },
  { label: "2-4", value: "2-4" },
  { label: "5 and more", value: "5 and more" },
];

const initialForm = {
  businessName: "",
  serviceProviderName: "",
  services: [],
  city: "",
  peopleCount: "1",
  googleReviewLink: "",
  preferredLanguage: [],
  reviewLength: "medium",
  reviewType: "auto",
  service_based: [],
  form_based: { questions: [] },
};

const BusinessInfoForm = ({ userName, onDataChange }) => {
  const [form] = Form.useForm();
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishingReview, setPublishingReview] = useState(false);
  const [existingReviewInfo, setExistingReviewInfo] = useState(null);
  const [showSuggestPanel, setShowSuggestPanel] = useState(false);
  const [serviceModalVisible, setServiceModalVisible] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm] = Form.useForm();
  const [shouldShowQRCode, setShouldShowQRCode] = useState(false);
  const [logoS3Key, setLogoS3Key] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [enableDirectReview, setEnableDirectReview] = useState(false);

  // Use ref to store onDataChange to prevent infinite loops
  const onDataChangeRef = useRef(onDataChange);
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const beforeUploadLogo = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG file!");
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("Image must be smaller than 2MB!");
      return false;
    }
    return false; // Prevent auto upload, we'll handle it manually
  };

  const handleLogoChange = async (info) => {
    if (info.file.status === "uploading") {
      setLogoUploading(true);
      return;
    }

    const file = info?.file?.originFileObj || info?.file;
    if (!file) return;

    setLogoUploading(true);
    setImagePreview(await getBase64(file));

    try {
      const uploadResult = await uploadProfilePic(file, userName);
      const newLogoKey = uploadResult?.key;
      setLogoS3Key(newLogoKey);

      // Debounced database update (similar to Miniwebsite)
      debouncedUpdateAccountDetails({
        logo: newLogoKey,
        username: userName,
      });
      setLogoUploading(false);
    } catch (e) {
      message.error("Failed to upload logo");
      setLogoUploading(false);
    }
  };

  const fetchReviewInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAccountDetails(userName);
      const account = res?.data?.getAccount || res?.getAccount || res;

      // Load logo from account (not from businessReviewInfo)
      setLogoS3Key(account?.logo || null);

      let reviewInfo = null;
      if (account && account.businessReviewInfo) {
        try {
          reviewInfo =
            typeof account.businessReviewInfo === "string"
              ? JSON.parse(account.businessReviewInfo)
              : account.businessReviewInfo;
        } catch (e) {
          reviewInfo = null;
        }
      }

      if (reviewInfo) {
        try {
          // Handle backward compatibility - ensure all required fields exist
          const safeReviewInfo = {
            ...initialForm,
            ...reviewInfo,
            reviewLength: reviewInfo.reviewLength || "medium",
            reviewType: reviewInfo.reviewType || "auto", // Default to auto for old users
            // Ensure service_based exists and is an array (for old users who only used auto)
            service_based: Array.isArray(reviewInfo.service_based)
              ? reviewInfo.service_based
              : [],
            // Form-based review questions
            form_based:
              reviewInfo.form_based && typeof reviewInfo.form_based === "object"
                ? {
                    questions: Array.isArray(reviewInfo.form_based.questions)
                      ? reviewInfo.form_based.questions
                      : [],
                  }
                : { questions: [] },
            // Load enableDirectReview flag (default to false for backward compatibility)
            enableDirectReview: reviewInfo.enableDirectReview || false,
            // Ensure services exists and is an array
            services: Array.isArray(reviewInfo.services)
              ? reviewInfo.services
              : typeof reviewInfo.services === "string"
              ? reviewInfo.services.split(",").filter(Boolean)
              : [],
            // Ensure preferredLanguage exists and is an array
            preferredLanguage: Array.isArray(reviewInfo.preferredLanguage)
              ? reviewInfo.preferredLanguage
              : typeof reviewInfo.preferredLanguage === "string"
              ? reviewInfo.preferredLanguage.split(",").filter(Boolean)
              : [],
          };

          setExistingReviewInfo(safeReviewInfo);
          setFormData(safeReviewInfo);
          // Set enableDirectReview state from loaded data
          setEnableDirectReview(safeReviewInfo.enableDirectReview || false);
          setShouldShowQRCode(true);
          onDataChangeRef.current({
            showQRCode: true,
            businessName: safeReviewInfo.businessName || userName || "",
          });
        } catch (error) {
          console.error("Error processing review info:", error);
          // Fallback to initial form if there's any error
          setExistingReviewInfo(null);
          setFormData(initialForm);
          setShouldShowQRCode(false);
          onDataChangeRef.current({
            showQRCode: false,
            businessName: userName || "",
          });
        }
      } else {
        setExistingReviewInfo(null);
        setFormData(initialForm);
        onDataChangeRef.current({
          showQRCode: false,
          businessName: userName || "",
        });
      }
    } catch (err) {
      console.error("Error fetching review info:", err);
    } finally {
      setLoading(false);
    }
  }, [userName]);

  const handleFormSubmit = async (values) => {
    setSaving(true);

    const trimString = (val) => (typeof val === "string" ? val.trim() : val);

    try {
      // Validate service-based mode
      const reviewType = trimString(
        values?.reviewType || formData?.reviewType || "auto"
      );

      if (reviewType === "service_based") {
        try {
          const serviceBased = Array.isArray(formData?.service_based)
            ? formData.service_based
            : [];
          if (!serviceBased || serviceBased.length === 0) {
            message.error(
              "Please add at least one service in Service Based mode!"
            );
            setSaving(false);
            return;
          }
        } catch (error) {
          console.error("Error validating service-based mode:", error);
          message.error("Error validating services. Please try again.");
          setSaving(false);
          return;
        }
      }

      if (reviewType === "form_based") {
        const formBased = formData?.form_based;
        const questions = Array.isArray(formBased?.questions)
          ? formBased.questions
          : [];
        if (!questions.length) {
          message.error("Please add at least one question in Form Based mode!");
          setSaving(false);
          return;
        }
      }

      try {
        // Safely extract and process form values with backward compatibility
        const reviewInfo = {
          businessName: trimString(
            values?.businessName || formData?.businessName || ""
          ),
          serviceProviderName: trimString(
            values?.serviceProviderName || formData?.serviceProviderName || ""
          ),
          city: trimString(values?.city || formData?.city || ""),
          peopleCount: trimString(
            values?.peopleCount || formData?.peopleCount || "1"
          ),
          googleReviewLink: trimString(
            values?.googleReviewLink || formData?.googleReviewLink || ""
          ),
          services: (() => {
            try {
              if (Array.isArray(values?.services)) {
                return values.services.map(trimString).filter(Boolean);
              }
              if (typeof values?.services === "string") {
                return values.services
                  .split(",")
                  .map(trimString)
                  .filter(Boolean);
              }
              // Fallback to formData for old users
              if (Array.isArray(formData?.services)) {
                return formData.services.map(trimString).filter(Boolean);
              }
              return [];
            } catch (error) {
              console.error("Error processing services:", error);
              return [];
            }
          })(),
          preferredLanguage: (() => {
            try {
              if (Array.isArray(values?.preferredLanguage)) {
                return values.preferredLanguage.map(trimString).filter(Boolean);
              }
              if (typeof values?.preferredLanguage === "string") {
                return values.preferredLanguage
                  .split(",")
                  .map(trimString)
                  .filter(Boolean);
              }
              // Fallback to formData for old users
              if (Array.isArray(formData?.preferredLanguage)) {
                return formData.preferredLanguage
                  .map(trimString)
                  .filter(Boolean);
              }
              return [];
            } catch (error) {
              console.error("Error processing preferredLanguage:", error);
              return [];
            }
          })(),
          reviewLength: trimString(
            values?.reviewLength || formData?.reviewLength || "medium"
          ),
          reviewType: reviewType,
          // Always preserve service_based data safely (default to empty array for old users)
          service_based: (() => {
            try {
              return Array.isArray(formData?.service_based)
                ? formData.service_based
                : [];
            } catch (error) {
              console.error("Error processing service_based:", error);
              return [];
            }
          })(),
          // Form-based review questions
          form_based: (() => {
            try {
              const fb = formData?.form_based;
              if (fb && typeof fb === "object" && Array.isArray(fb.questions)) {
                return { questions: fb.questions };
              }
              return { questions: [] };
            } catch (error) {
              console.error("Error processing form_based:", error);
              return { questions: [] };
            }
          })(),
          // Store enableDirectReview flag
          enableDirectReview: enableDirectReview || false,
          // Logo is saved separately to account details, not in businessReviewInfo
        };

        if (reviewInfo) {
          try {
            await updateAccountDetails({
              username: userName,
              businessReviewInfo: JSON.stringify(reviewInfo),
            });
          } catch (error) {
            console.error("Error updating account details:", error);
            message.error(
              "Failed to save business information. Please try again."
            );
            setSaving(false);
            return;
          }

          setPublishingReview(true);

          try {
            if (reviewInfo.reviewType === "form_based") {
              // Form-based: just publish the page (no review generation - reviews are live)
              await API.get("REST", "/publish");
              message.success("Form saved and published successfully!");
            } else {
              // Call appropriate API based on review type
              const apiEndpoint =
                reviewInfo.reviewType === "service_based"
                  ? "/publish/generate-service-based-reviews"
                  : "/publish/generate-reviews";

              await API.post("REST", apiEndpoint, {
                body: { reviewInfo },
              });
              message.success("Reviews are generated successfully!");
            }

            setExistingReviewInfo(reviewInfo);
            setFormData(reviewInfo);
            setShouldShowQRCode(true);
            onDataChangeRef.current({
              showQRCode: true,
              businessName: reviewInfo.businessName || userName || "",
            });
          } catch (apiError) {
            console.error("Error calling review generation API:", apiError);
            throw apiError; // Re-throw to be caught by outer catch
          }
        } else {
          message.error("Business review info not found!");
          setSaving(false);
          return;
        }
      } catch (dataError) {
        console.error("Error processing form data:", dataError);
        message.error("Error processing form data. Please try again.");
        setSaving(false);
        return;
      }
    } catch (err) {
      console.error("Error in form submission:", err);
      message.error(
        err?.errors?.[0]?.message ||
          "Error in Generating reviews, please try again or contact support."
      );
    } finally {
      setSaving(false);
      setPublishingReview(false);
    }
  };

  const addServiceKeyword = (keyword) => {
    const current = form.getFieldValue("services") || [];
    if (!keyword) return;
    // Check if keyword already exists (case-insensitive)
    const normalizedKeyword = String(keyword).trim().toLowerCase();
    const alreadyExists = current.some((service) => {
      const normalizedService = String(service).trim().toLowerCase();
      return normalizedService === normalizedKeyword;
    });
    if (alreadyExists) return;
    const updated = [...current, keyword];
    form.setFieldsValue({ services: updated });
  };

  // Service Based Management Functions
  const handleAddService = () => {
    setEditingService(null);
    serviceForm.resetFields();
    setServiceModalVisible(true);
  };

  const handleEditService = (service) => {
    try {
      if (!service || !service.id) {
        console.error("Invalid service object provided for editing");
        message.error("Invalid service data. Please try again.");
        return;
      }
      setEditingService(service);
      serviceForm.setFieldsValue({
        name: service.name || "",
        description: service.description || "",
      });
      setServiceModalVisible(true);
    } catch (error) {
      console.error("Error editing service:", error);
      message.error("Error opening service editor. Please try again.");
    }
  };

  const handleDeleteService = (serviceId) => {
    try {
      // Safely handle service_based array for backward compatibility
      const currentServices = Array.isArray(formData?.service_based)
        ? formData.service_based
        : [];

      const updatedServices = currentServices.filter(
        (service) => service && service.id !== serviceId
      );

      setFormData({ ...formData, service_based: updatedServices });
      form.setFieldsValue({ service_based: updatedServices });
    } catch (error) {
      console.error("Error deleting service:", error);
      message.error("Error deleting service. Please try again.");
    }
  };

  const handleServiceSubmit = (values) => {
    try {
      const { name, description } = values;

      // Safely get current services with backward compatibility
      const currentServices = Array.isArray(formData?.service_based)
        ? formData.service_based
        : [];

      if (editingService) {
        try {
          // Edit existing service
          const updatedServices = currentServices.map((service) => {
            if (service && service.id === editingService.id) {
              return {
                ...service,
                name: (name || "").trim(),
                description: (description || "").trim(),
              };
            }
            return service;
          });
          setFormData({ ...formData, service_based: updatedServices });
          form.setFieldsValue({ service_based: updatedServices });
        } catch (error) {
          console.error("Error editing service:", error);
          message.error("Error updating service. Please try again.");
          return;
        }
      } else {
        try {
          // Add new service
          if (currentServices.length >= 5) {
            message.warning("You can add maximum 5 services!");
            return;
          }
          const newService = {
            id: Date.now().toString(),
            name: (name || "").trim(),
            description: (description || "").trim(),
          };
          const updatedServices = [...currentServices, newService];
          setFormData({ ...formData, service_based: updatedServices });
          form.setFieldsValue({ service_based: updatedServices });
        } catch (error) {
          console.error("Error adding service:", error);
          message.error("Error adding service. Please try again.");
          return;
        }
      }

      setServiceModalVisible(false);
      serviceForm.resetFields();
      setEditingService(null);
    } catch (error) {
      console.error("Error in handleServiceSubmit:", error);
      message.error("An error occurred. Please try again.");
    }
  };

  const handleTabChange = (activeKey) => {
    setFormData({ ...formData, reviewType: activeKey });
    form.setFieldsValue({ reviewType: activeKey });
  };

  useEffect(() => {
    if (userName) {
      fetchReviewInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userName]);

  useEffect(() => {
    // Only set form values when existingReviewInfo is set for the first time
    // This prevents infinite loops from form.setFieldsValue triggering re-renders
    if (existingReviewInfo) {
      try {
        // Safely set form values with backward compatibility
        const safeFormData = {
          ...formData,
          // Ensure service_based is an array for old users
          service_based: Array.isArray(formData?.service_based)
            ? formData.service_based
            : [],
          // Ensure services is an array
          services: Array.isArray(formData?.services) ? formData.services : [],
          // Ensure form_based exists
          form_based:
            formData?.form_based && typeof formData.form_based === "object"
              ? {
                  questions: Array.isArray(formData.form_based.questions)
                    ? formData.form_based.questions
                    : [],
                }
              : { questions: [] },
        };
        form.setFieldsValue(safeFormData);
      } catch (error) {
        console.error("Error setting form values:", error);
        // Try to set at least the basic fields
        try {
          form.setFieldsValue({
            businessName: formData?.businessName || "",
            serviceProviderName: formData?.serviceProviderName || "",
            city: formData?.city || "",
            peopleCount: formData?.peopleCount || "1",
            googleReviewLink: formData?.googleReviewLink || "",
            preferredLanguage: Array.isArray(formData?.preferredLanguage)
              ? formData.preferredLanguage
              : [],
            reviewLength: formData?.reviewLength || "medium",
            reviewType: formData?.reviewType || "auto",
            services: Array.isArray(formData?.services)
              ? formData.services
              : [],
            service_based: [],
            form_based: { questions: [] },
          });
        } catch (fallbackError) {
          console.error("Error in fallback form value setting:", fallbackError);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingReviewInfo]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-4 lg:p-6 flex items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-4 lg:p-6">
      <div className="flex justify-center items-center mb-4">
        <h2 className="text-lg font-bold">Business Information</h2>
        <Tooltip
          title={
            <>
              <div>
                We use your business information only to generate high-quality,
                authentic reviews tailored to your services and location.
                <br />
                <b>Your privacy is our top priority.</b>
                <br />
                We will never share your data with anyone.
                <br />
                Our goal is to help you grow your Google Business
                ranking—securely and ethically.
              </div>
            </>
          }
          placement="top">
          <InfoCircleOutlined className="ml-2 text-blue-500 cursor-pointer" />
        </Tooltip>
      </div>

      {publishingReview && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center flex items-center justify-center gap-2">
          <Loader size="middle" />
          <span className="text-green-700 font-medium">
            Generating your reviews for you business...
          </span>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={formData}
        onFinish={handleFormSubmit}
        onValuesChange={(changedValues, allValues) => {
          // Update QR code in real-time when business name changes
          if (changedValues.businessName !== undefined) {
            const newBusinessName =
              changedValues.businessName || userName || "";
            // Show QR code if there's a business name (for real-time preview)
            const hasBusinessName = newBusinessName.trim().length > 0;
            onDataChangeRef.current({
              showQRCode:
                hasBusinessName ||
                shouldShowQRCode ||
                existingReviewInfo !== null,
              businessName: newBusinessName,
            });
          }
        }}>
        {/* Business Logo */}
        <Form.Item
          className="mb-4"
          label={<span className="text-sm">Business Logo</span>}>
          <Upload
            name="logo"
            listType="picture-circle"
            className={`avatar-uploader ${
              !logoS3Key && !imagePreview ? "avatar-uploader-empty" : ""
            }`}
            showUploadList={false}
            beforeUpload={beforeUploadLogo}
            onChange={handleLogoChange}
            accept="image/*"
            style={{
              width: 92,
              height: 92,
            }}>
            {logoS3Key || imagePreview ? (
              <UploadProfilePic
                S3Key={logoS3Key}
                src={imagePreview}
                alt="Business Logo"
                className="rounded-full object-cover"
                width={92}
                height={92}
              />
            ) : (
              <div className="flex flex-col items-center justify-center border border-gray-300 h-[92px] w-[92px] rounded-full">
                {logoUploading ? <LoadingOutlined /> : <PlusOutlined />}
                <div className="text-xs mt-1">Add Logo</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        {/* Business Name */}
        <Form.Item
          label="Business Name"
          name="businessName"
          rules={[
            {
              required: true,
              message: "Please enter business name",
            },
          ]}>
          <Input
            size="large"
            placeholder="Enter your business name"
            className="input-border bg-gray-100 border-gray-200 focus:bg-white"
          />
        </Form.Item>

        {/* Service Provider Name */}
        <Form.Item label="Service Provider Name" name="serviceProviderName">
          <Input
            size="large"
            placeholder="Enter your name (optional)"
            className="input-border bg-gray-100 border-gray-200 focus:bg-white"
            autoFocus
          />
        </Form.Item>

        {/* Review Type Selection */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <Form.Item label="Review Type" name="reviewType" className="mb-0">
            <Segmented
              options={[
                { label: "Auto", value: "auto" },
                { label: "Service Based", value: "service_based" },
                { label: "Form Based", value: "form_based" },
              ]}
              value={formData.reviewType}
              onChange={handleTabChange}
              size="large"
              style={{
                width: "100%",
              }}
              block
              className="w-full [&_.ant-segmented-item]:text-center [&_.ant-segmented-item]:flex-1 mb-4"
            />
          </Form.Item>

          {/* Auto Mode Content */}
          {formData.reviewType === "auto" && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="ant-form-item-required text-[rgba(0,0,0,0.88)]">
                    Add services keywords
                  </label>
                  <button
                    type="button"
                    className="text-blue-600 hover:text-blue-700 text-sm"
                    onClick={() => setShowSuggestPanel((v) => !v)}>
                    {showSuggestPanel ? "Hide" : "AI Suggestion"}
                  </button>
                </div>

                <Form.Item
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.services !== currentValues.services
                  }>
                  {() => (
                    <AISuggestionsPanel
                      showSuggestPanel={showSuggestPanel}
                      onToggle={setShowSuggestPanel}
                      businessName={
                        form.getFieldValue("businessName") ||
                        formData.businessName
                      }
                      onAddKeyword={addServiceKeyword}
                      existingServices={form.getFieldValue("services") || []}
                    />
                  )}
                </Form.Item>

                <Form.Item
                  label=""
                  name="services"
                  rules={[
                    {
                      required: formData.reviewType === "auto",
                      message: "Please enter at least one service",
                    },
                  ]}>
                  <Select
                    mode="tags"
                    style={{ width: "100%" }}
                    placeholder="Type a service and press Enter"
                    tokenSeparators={[","]}
                    options={[]}
                    open={false}
                    suffixIcon={null}
                    size="large"
                    className="border-gray-200 focus:bg-white"
                  />
                </Form.Item>
              </div>
            </div>
          )}

          {/* Service Based Mode Content */}
          {formData.reviewType === "service_based" && (
            <div className="space-y-4">
              {/* Direct Review Toggle */}
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={enableDirectReview}
                    onChange={(checked) => {
                      setEnableDirectReview(checked);
                      // Update formData to persist the state
                      setFormData({ ...formData, enableDirectReview: checked });
                    }}
                    className="bg-gray-300"
                  />
                  <label className="text-[rgba(0,0,0,0.88)] font-medium">
                    Write Review Button{" "}
                    <span className="text-gray-500">
                      (directly go to review page)
                    </span>
                  </label>
                </div>
              </div>

              {/* Service Selection Section */}
              <div className="flex items-center justify-between">
                <label className="text-[rgba(0,0,0,0.88)]">
                  Add multiple service
                </label>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddService}
                  disabled={
                    (Array.isArray(formData?.service_based)
                      ? formData.service_based.length
                      : 0) >= 5
                  }
                  className="rounded-lg">
                  Add Service
                </Button>
              </div>

              <Form.Item name="service_based" hidden>
                <Input />
              </Form.Item>

              {(() => {
                try {
                  const services = Array.isArray(formData?.service_based)
                    ? formData.service_based
                    : [];

                  return services.length > 0 ? (
                    <div className="space-y-3">
                      {services
                        .filter((service) => service && service.id)
                        .map((service) => (
                          <Card
                            key={service.id}
                            size="small"
                            className="border border-gray-200 hover:border-primary bg-gray-100">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-800 mb-1">
                                  {service.name || "Unnamed Service"}
                                </h4>
                                <p className="text-gray-600 text-sm">
                                  {service.description || "No description"}
                                </p>
                              </div>
                              <Space>
                                <Button
                                  type="text"
                                  icon={<EditOutlined />}
                                  onClick={() => handleEditService(service)}
                                  className="text-blue-600 hover:text-blue-700"
                                />
                                <Button
                                  type="text"
                                  icon={<DeleteOutlined />}
                                  onClick={() =>
                                    handleDeleteService(service.id)
                                  }
                                  className="text-red-600 hover:text-red-700"
                                />
                              </Space>
                            </div>
                          </Card>
                        ))}
                    </div>
                  ) : null;
                } catch (error) {
                  console.error("Error rendering services:", error);
                  return null;
                }
              })() || (
                <div className="text-center py-4 text-gray-500">
                  <p>
                    No services added yet. Click "Add Service" to get started.
                  </p>
                </div>
              )}

              {(Array.isArray(formData?.service_based)
                ? formData.service_based.length
                : 0) >= 5 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-800 text-sm">
                    <strong>Maximum reached:</strong> You can add up to 5
                    services.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Form Based Mode Content */}
          {formData.reviewType === "form_based" && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 text-sm">
                  <strong>Form Based:</strong> Customers answer your questions,
                  then we generate a personalized review from their answers. No
                  pre-generated reviews—each review is unique.
                </p>
              </div>
              <Form.Item name="form_based" hidden>
                <Input />
              </Form.Item>
              <FormBuilder
                questions={
                  Array.isArray(formData?.form_based?.questions)
                    ? formData.form_based.questions
                    : []
                }
                onChange={(questions) => {
                  const updated = {
                    ...formData,
                    form_based: { questions },
                  };
                  setFormData(updated);
                  form.setFieldsValue({ form_based: { questions } });
                }}
                disabled={publishingReview}
              />
            </div>
          )}
        </div>

        {/* City / Area */}
        <Form.Item label="City / Area" name="city">
          <Input
            size="large"
            placeholder="e.g. Mini Bazar, Surat, Gujarat"
            className="input-border bg-gray-100 border-gray-200 focus:bg-white"
          />
        </Form.Item>

        {/* Working People */}
        <Form.Item label="Working People" name="peopleCount">
          <Radio.Group block options={peopleOptions} optionType="button" />
        </Form.Item>

        {/* Google Review Link */}
        <Form.Item
          label="Google Review Link"
          name="googleReviewLink"
          rules={[
            {
              required: true,
              message: "Please enter your Google Review link",
            },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                // Check if URL starts with http:// or https://
                if (!value.trim().match(/^https?:\/\//i)) {
                  return Promise.reject(
                    new Error(
                      "Enter a valid URL starting with http:// or https:// 😊"
                    )
                  );
                }
                // Validate it's a proper URL format
                try {
                  new URL(value.trim());
                  return Promise.resolve();
                } catch {
                  return Promise.reject(
                    new Error(
                      "Enter a valid URL starting with http:// or https:// 😊"
                    )
                  );
                }
              },
            },
          ]}>
          <Input
            size="large"
            placeholder="e.g. https://g.page/r/CdYd3Zqk8UdvXyz/review or https://search.google.com/local/writereview?placeid=..."
            className="input-border bg-gray-100 border-gray-200 focus:bg-white"
          />
        </Form.Item>

        {/* Preferred Language */}
        <Form.Item
          label="Preferred Language"
          name="preferredLanguage"
          rules={[
            {
              required: true,
              message: "Please select at least one language",
            },
          ]}>
          <Select
            mode="multiple"
            style={{ width: "100%", backgroundColor: "#f3f4f6" }}
            placeholder="Type or select languages"
            tokenSeparators={[","]}
            options={languages}
            suffixIcon={<FontAwesomeIcon icon={faChevronDown} />}
            filterOption={(input, option) =>
              (option.label || option.value || "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            notFoundContent={null}
            className="border-gray-200 focus:bg-white"
          />
        </Form.Item>

        {/* Review Length */}
        <Form.Item label="Review Length" name="reviewLength">
          <Select
            style={{ width: "100%", backgroundColor: "#f3f4f6" }}
            options={[
              { label: "Short (1-2 lines)", value: "short" },
              { label: "Medium (3-4 lines)", value: "medium" },
              { label: "Long (5-7 lines)", value: "long" },
            ]}
            suffixIcon={<FontAwesomeIcon icon={faChevronDown} />}
            className="border-gray-200 focus:bg-white"
          />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          className="rounded-xl px-6 mt-3 font-bold w-full"
          loading={saving || publishingReview}>
          {publishingReview
            ? "Generating Reviews..."
            : saving
            ? "Saving..."
            : "Generate Reviews"}
        </Button>
      </Form>

      {/* Service Modal */}
      <Modal
        title={editingService ? "Edit Service" : "Add New Service"}
        open={serviceModalVisible}
        onCancel={() => {
          setServiceModalVisible(false);
          serviceForm.resetFields();
          setEditingService(null);
        }}
        footer={null}
        className="service-modal">
        <Form
          form={serviceForm}
          layout="vertical"
          onFinish={handleServiceSubmit}
          className="mt-4">
          <Form.Item
            label="Service Name"
            name="name"
            rules={[
              { required: true, message: "Please enter service name" },
              { min: 2, message: "Service name must be at least 2 characters" },
            ]}>
            <Input
              size="large"
              placeholder="e.g. Hair Cutting, Car Repair, Consulting"
              className="input-border bg-gray-100 border-gray-200 focus:bg-white"
            />
          </Form.Item>

          <Form.Item
            label="Service Description"
            name="description"
            rules={[
              { required: true, message: "Please enter service description" },
              {
                min: 10,
                message: "Description must be at least 10 characters",
              },
            ]}>
            <Input.TextArea
              rows={4}
              placeholder="Describe what this service includes, its benefits, and what customers can expect..."
              className="input-border bg-gray-100 border-gray-200 focus:bg-white"
            />
          </Form.Item>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              onClick={() => {
                setServiceModalVisible(false);
                serviceForm.resetFields();
                setEditingService(null);
              }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className="rounded-lg px-6">
              {editingService ? "Update Service" : "Add Service"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default BusinessInfoForm;
